'use client';
import type { SelectionSummaryProps, SummaryChipProps } from 'elowen-plugin-ui-kit';
import { Settings2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from './Button';
import { MarkStrip, type Mark } from './MarkStrip';
import { LabelMark } from './StatusMark';
import { useHorizontalFold } from './horizontalScroll';

/** One chip of a summary. Exported so a caller that owns a fact this component cannot see can still put
 *  it in the same row looking like everything beside it. */
export function SummaryChip({ icon, label, category }: SummaryChipProps) {
  if (category) return <span data-selection-category title={label}><LabelMark tone={category.tone} dot={category.dot}>{label}</LabelMark></span>;
  return (
    <span className="inline-flex max-w-full items-center gap-1.5 rounded-md border border-border bg-muted px-2 py-0.5 text-small text-muted-foreground">
      {icon ? <span aria-hidden className="shrink-0">{icon}</span> : null}
      <span className="truncate" title={label}>{label}</span>
    </span>
  );
}

/** The one on-page look of a managed selection: a card with a count line, a chip row and a "Manage" button
 *  that opens the ManageSelectionModal, the only place the selection is edited. The chip row is always one
 *  line: as many sample chips as fit its width stay whole, and the rest fold into the trailing "+N" chip,
 *  which counts them together with `moreCount`. `marks` replaces the chips with a row of brand marks for
 *  model selections; it is web-only and not part of the plugin kit type. The count line is the selection's
 *  value, so it reads at the value role (`text-ui`) of the Settings row the summary stands in. */
export function SelectionSummary({ countText, samples, moreCount, onManage, manageLabel, manageAriaLabel, disabled = false, marks }: SelectionSummaryProps & { marks?: { kind: string; marks: readonly Mark[] } }) {
  const chipsRendered = !marks && (samples.length > 0 || moreCount > 0);
  const chips = useHorizontalFold<HTMLDivElement>({ itemCount: samples.length, moreCount, enabled: chipsRendered });
  const folded = samples.length - chips.shown + moreCount;
  return (
    <div
      data-selection-summary
      className="flex w-full min-w-0 flex-wrap items-center gap-x-4 gap-y-2 rounded-xl border border-border bg-card px-3.5 py-3 shadow-card"
    >
      <div className="flex min-w-0 flex-[1_1_--spacing(56)] flex-col gap-1.5">
        {countText ? <span className="text-ui tabular-nums text-foreground">{countText}</span> : null}
        {(marks ? marks.marks.length > 0 : samples.length > 0 || moreCount > 0) && (
          <div ref={chips.ref} data-selection-chips className="relative flex min-w-0 flex-nowrap items-center gap-1.5 overflow-hidden">
            {marks ? (
              // The count is already the visible line above, so the strip carries no second copy of it.
              <MarkStrip kind={marks.kind} marks={marks.marks} />
            ) : (
              <>
                {samples.map((s, index) => (
                  // A folded chip stays measurable but takes no room. The lone shown chip may shrink, so a
                  // long label truncates instead of folding the row's only item away.
                  <span
                    key={s.id ?? s.label}
                    className={cn(index >= chips.shown ? 'invisible absolute' : chips.shown === 1 ? 'flex min-w-0 max-w-full shrink' : 'flex max-w-full shrink-0')}
                  >
                    <SummaryChip icon={s.icon} label={s.label} category={s.category} />
                  </span>
                ))}
                <span className={cn('rounded-md border border-border bg-muted px-2 py-0.5 text-small tabular-nums text-muted-foreground', folded > 0 ? 'shrink-0' : 'invisible absolute')}>+{folded}</span>
              </>
            )}
          </div>
        )}
      </div>
      <Button type="button" variant="outline" size="sm" icon={Settings2} data-selection-manage onClick={onManage} disabled={disabled} aria-label={manageAriaLabel} className="shrink-0">
        {manageLabel}
      </Button>
    </div>
  );
}
